import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Upload, FileText, X, Loader2, ExternalLink, CloudUpload } from "lucide-react";
import { uploadFile, fileUrl } from "@/lib/api";
import { toast } from "sonner";

export default function FileUploadField({ label, value, onChange, testid, accept = ".pdf,.jpg,.jpeg,.png,.webp" }) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadFile(file);
      onChange(res.path);
      toast.success("Lampiran berhasil diunggah");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengunggah file");
    } finally {
      setUploading(false);
      if (ref.current) ref.current.value = "";
    }
  };

  const onPick = (e) => doUpload(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    doUpload(e.dataTransfer.files?.[0]);
  };
  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);

  return (
    <div className="space-y-1.5">
      <label className="text-xs uppercase tracking-wider font-semibold text-slate-600 block">{label}</label>
      {value ? (
        <div className="flex items-center gap-2 border-2 border-emerald-300 bg-emerald-50/40 rounded-md px-3 py-3">
          <div className="w-9 h-9 shrink-0 rounded-md bg-white border border-emerald-200 flex items-center justify-center">
            <FileText className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-emerald-800">File terunggah</div>
            <a href={fileUrl(value)} target="_blank" rel="noreferrer"
               className="text-sm text-[#134074] hover:underline inline-flex items-center gap-1 truncate"
               data-testid={`${testid}-view`}>
              Lihat lampiran <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)}
                  className="text-slate-500 hover:text-red-600" data-testid={`${testid}-remove`}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <>
          <button
            type="button"
            onClick={() => ref.current?.click()}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            disabled={uploading}
            className={`w-full flex flex-col items-center justify-center gap-2 px-4 py-6 rounded-md border-2 border-dashed transition-all ${
              dragOver
                ? "border-[#C59B27] bg-amber-50"
                : "border-slate-300 bg-white hover:border-[#0B2545] hover:bg-slate-50"
            }`}
            data-testid={`${testid}-dropzone`}
          >
            {uploading ? (
              <>
                <Loader2 className="w-7 h-7 text-[#0B2545] animate-spin" />
                <div className="text-sm font-medium text-slate-700">Mengunggah…</div>
              </>
            ) : (
              <>
                <div className="w-11 h-11 rounded-full bg-[#0B2545]/5 flex items-center justify-center">
                  <CloudUpload className="w-6 h-6 text-[#0B2545]" />
                </div>
                <div className="text-sm font-semibold text-[#0B2545]">
                  Klik untuk pilih file <span className="text-slate-500 font-normal">atau tarik ke sini</span>
                </div>
                <div className="text-[11px] text-slate-500 flex items-center gap-1">
                  <Upload className="w-3 h-3" /> PDF · JPG · PNG · WebP · Maks. 10 MB
                </div>
              </>
            )}
          </button>
          <input
            ref={ref} type="file" accept={accept} onChange={onPick}
            className="hidden" data-testid={testid} disabled={uploading}
          />
        </>
      )}
    </div>
  );
}
